import { z } from 'zod'

/**
 * A configuração da máquina, quando existe uma máquina.
 *
 * ## O que este arquivo detecta
 *
 * O aplicativo desktop serve a mesma aplicação a partir de um servidor local
 * (`apps/desktop/src-tauri/src/proxy.rs`), e esse servidor responde a
 * `/__aeris/config` — qual servidor Aeris esta instalação usa. Num navegador
 * comum esse endereço não existe, e o 404 é a resposta: **não há shell aqui**.
 *
 * É por isso que a detecção é um `fetch` e não `isDesktop()`. A pergunta que
 * importa não é "estou dentro do Tauri?", é "existe uma configuração de máquina
 * que eu possa ler e gravar?". As duas se separam no dia em que o mesmo
 * servidor local existir fora do Tauri — e, mais concretamente, um `fetch`
 * dispensa mock de `window.__TAURI_INTERNALS__` em todo teste que passa por
 * aqui.
 *
 * ## Por que não IPC
 *
 * O `invoke` do Tauri é negado para páginas servidas por URL remota, e a página
 * do bundle é servida por `http://127.0.0.1:<porta>` — remota para o Tauri.
 * Liberar exigiria conceder permissões de sistema a um padrão de URL. O proxy
 * já é a costura de mesma origem; a configuração passa por ele.
 */
export const ShellConfig = z.object({
  /** O Aeris Cloud. Vazio na primeira execução. */
  serverUrl: z.string(),
  /** A porta do servidor local. Só de leitura: quem manda nela é o shell. */
  port: z.number().int().positive().nullable().optional(),
  /** O Aeris Edge da loja. Vazio quando não há um (F042/F155). */
  edgeUrl: z.string(),
  /**
   * A versão do aplicativo instalado (F155).
   *
   * Vem daqui, e não do `getVersion()` do Tauri, porque esta página é servida
   * por `http://127.0.0.1:<porta>` — remota para o Tauri, que nega `invoke` a
   * URLs remotas. Opcional porque um shell de uma versão anterior não a manda,
   * e nesse caso a tela "Sobre" diz que não sabe em vez de inventar.
   */
  version: z.string().optional(),
  /**
   * Se este caixa tem a credencial do Edge (F197). O token em si mora no
   * shell e nunca vem para a página. Ausente num shell anterior: não pareado.
   */
  edgePaired: z.boolean().optional(),
})

export type ShellConfig = z.infer<typeof ShellConfig>

const ENDPOINT = '/__aeris/config'

/**
 * A configuração desta máquina, ou `null` quando não há shell.
 *
 * Nunca lança: um navegador comum devolve 404, e uma falha de rede aqui é
 * indistinguível de "não é o desktop" — nos dois casos a resposta certa é
 * seguir como aplicação web.
 */
export const readShellConfig = async (
  signal?: AbortSignal
): Promise<ShellConfig | null> => {
  try {
    const response = await fetch(ENDPOINT, {
      // `exactOptionalPropertyTypes` recusa `signal: undefined`, e o `fetch`
      // distingue os dois: ausente é "sem cancelamento", `undefined` não é.
      ...(signal === undefined ? {} : { signal }),
      headers: { accept: 'application/json' },
    })

    if (!response.ok) return null

    const parsed = ShellConfig.safeParse(await response.json())
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

export class ShellConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ShellConfigError'
  }
}

/**
 * Grava o servidor desta máquina.
 *
 * `Content-Type: application/json` não é decoração: o shell recusa a escrita
 * sem ele, porque um formulário HTML de outra página só consegue mandar
 * `form-urlencoded`, `multipart` ou `text/plain`. É metade da proteção do
 * endpoint; a outra metade é o `Origin`, que o navegador preenche sozinho.
 */
export const writeShellConfig = async (
  input: Pick<ShellConfig, 'serverUrl'> & Partial<Pick<ShellConfig, 'edgeUrl'>>
): Promise<ShellConfig> => {
  const response = await fetch(ENDPOINT, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      serverUrl: input.serverUrl,
      edgeUrl: input.edgeUrl ?? '',
    }),
  })

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null)
    const message =
      typeof body === 'object' &&
      body !== null &&
      'message' in body &&
      typeof body.message === 'string'
        ? body.message
        : 'Não foi possível gravar a configuração nesta máquina.'

    throw new ShellConfigError(message)
  }

  const parsed = ShellConfig.safeParse(await response.json())
  if (!parsed.success) {
    throw new ShellConfigError('O shell devolveu uma configuração ilegível.')
  }

  return parsed.data
}

/**
 * Entrega ao shell a credencial do Edge (F197), ou `null` para despareá-lo.
 *
 * Manda só o token: o shell deixa como estão os endereços que não vieram, e a
 * tela do servidor não perde o que a pessoa configurou.
 */
export const writeEdgeToken = async (token: string | null): Promise<void> => {
  const response = await fetch(ENDPOINT, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ edgeToken: token }),
  })
  if (!response.ok) {
    throw new ShellConfigError(
      'Não foi possível guardar o pareamento nesta máquina.'
    )
  }
}
